import type { IntlShape } from 'react-intl';
import {
  eventRegistrationFormMessages as m,
  getMailDefaults,
} from './messages';
import {
  buildContactAllowedInitialData,
  buildDataprotectionInfoInitialData,
  buildPhonenumberInitialData,
} from '../../../config/specialFormFields';
import { DEFAULT_CAPTCHA_PROVIDER } from '../../../config/captchaDefault';

export const EVENT_REGISTRATION_FORM_BLOCK_ID = 'eventRegistrationForm';

// Field IDs are also used by the panel/form transport (issue #1663) to map
// occasion data into the hidden fields. Keep these stable.
export const FIELD_IDS = {
  firstName: 'vorname',
  lastName: 'nachname',
  email: 'email',
  phone: 'telefonnummer',
  street: 'strasse',
  zip: 'plz',
  city: 'ort',
  dataProtectionInfo: 'dataprotectioninfo',
  contactAllowed: 'contact_allowed',
  hiddenEventName: 'eventname',
  hiddenOccasionName: 'durchfuehrung',
  hiddenOccasionDate: 'datum',
  // Spec lists "ort" twice (visible city + hidden occasion place). Renamed
  // hidden key to avoid the collision with the visible "ort" field.
  hiddenOccasionPlace: 'durchfuehrungsort',
} as const;

// Core identity fields the schema editor keeps non-editable and non-removable
// (issue #1662). They stay draggable/re-orderable; only their edit + delete
// controls are suppressed. Consumed via LockedSchemaFieldsContext in
// EditSchemaForm.
//
// NOTE: the newsletter opt-in (contact_allowed) is deliberately NOT locked.
// HS-Testing feedback (#1662 note_806351) requires it to ship in the block by
// default yet stay editable and removable by editors, so it must keep its
// edit/delete controls even though it is required by default.
export const LOCKED_EVENT_REGISTRATION_FIELD_IDS: string[] = [
  FIELD_IDS.firstName,
  FIELD_IDS.lastName,
  FIELD_IDS.email,
];

type FieldDef = {
  id: string;
  title: string;
  factory: string;
  type: string;
  widget?: string;
  choices?: [string, string][];
  default?: { data: string };
  required?: boolean;
};

const buildSchema = (intl: IntlShape) => {
  const t = (msg: { id: string; defaultMessage: string }) =>
    intl.formatMessage(msg);

  const visibleFields: FieldDef[] = [
    {
      id: FIELD_IDS.firstName,
      title: t(m.firstName),
      factory: 'label_text_field',
      type: 'string',
      required: true,
    },
    {
      id: FIELD_IDS.lastName,
      title: t(m.lastName),
      factory: 'label_text_field',
      type: 'string',
      required: true,
    },
    {
      id: FIELD_IDS.email,
      title: t(m.email),
      factory: 'label_email',
      type: 'string',
      widget: 'email',
      required: true,
    },
    {
      ...buildPhonenumberInitialData(intl),
      id: FIELD_IDS.phone,
      title: t(m.phone),
      required: false,
    },
    {
      id: FIELD_IDS.street,
      title: t(m.street),
      factory: 'label_text_field',
      type: 'string',
      required: false,
    },
    {
      id: FIELD_IDS.zip,
      title: t(m.zip),
      factory: 'label_text_field',
      type: 'string',
      required: false,
    },
    {
      id: FIELD_IDS.city,
      title: t(m.city),
      factory: 'label_text_field',
      type: 'string',
      required: false,
    },
    {
      ...buildDataprotectionInfoInitialData(intl),
      id: FIELD_IDS.dataProtectionInfo,
      title: t(m.dataProtectionInfoTitle),
      required: false,
    },
    {
      ...buildContactAllowedInitialData(intl),
      id: FIELD_IDS.contactAllowed,
      title: t(m.contactAllowed),
      required: true,
    },
  ];

  const hiddenFields: FieldDef[] = [
    {
      id: FIELD_IDS.hiddenEventName,
      title: t(m.eventName),
      factory: 'hidden',
      type: 'string',
      widget: 'hidden',
    },
    {
      id: FIELD_IDS.hiddenOccasionName,
      title: t(m.occasionName),
      factory: 'hidden',
      type: 'string',
      widget: 'hidden',
    },
    {
      id: FIELD_IDS.hiddenOccasionDate,
      title: t(m.occasionDate),
      factory: 'hidden',
      type: 'string',
      widget: 'hidden',
    },
    {
      id: FIELD_IDS.hiddenOccasionPlace,
      title: t(m.occasionPlace),
      factory: 'hidden',
      type: 'string',
      widget: 'hidden',
    },
  ];

  const allFields = [...visibleFields, ...hiddenFields];

  const properties: Record<string, any> = {};
  for (const f of allFields) {
    const { id, required: _r, ...rest } = f;
    properties[id] = rest;
  }

  return {
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: allFields.map((f) => f.id),
      },
    ],
    properties,
    required: visibleFields.filter((f) => f.required).map((f) => f.id),
  };
};

export const getDefaultEventRegistrationFormData = (intl: IntlShape) => {
  // Mail texts follow the page language (note_800770): a form created on an
  // EN/FR page bakes in EN/FR confirmation texts, German elsewhere.
  const mail = getMailDefaults(intl.locale);
  return {
    '@type': 'schemaForm', // morph target — NOT EVENT_REGISTRATION_FORM_BLOCK_ID
    eventRegistration: true, // marker for the panel (#1663) + locked core fields / mail-config edit restrictions (#1662)
    schema: buildSchema(intl),
    submit_label: intl.formatMessage(m.submitLabel), // spec #1662: "Anmeldung abschicken" (overrides the platform default "Senden")
    captcha: DEFAULT_CAPTCHA_PROVIDER, // #1667: protect registrations by default
    send_confirmation: true,
    confirmation_recipients: FIELD_IDS.email,
    mail_template: 'default',
    success: mail.success,
    thankyou: mail.thankyou,
    subject: mail.subject,
    // richtext value, NOT a plain string — the backend email processor reads
    // block.mail_header.get("data") and crashes on a str (#1662).
    mail_header: { data: mail.mailHeader },
  };
};
